import { loadRuntimeConfig } from "@crialume/core";
import type { RuntimeConfig } from "@crialume/sdk";

/** Rotas de autenticação do backend (`AuthController`), relativas a `apiBaseUrl`. */
export interface AuthEndpoints {
  signIn: string;
  refresh: string;
  signOut: string;
  me: string;
  changePassword: string;
}

export interface AppConfig extends RuntimeConfig {
  /** Campanha sazonal: ID explícito do pacote publicado e cupom opcional. */
  campaigns?: { natal?: { bundleId?: string; coupon?: string } };
  auth: {
    /** "jwt" usa o backend; "mock" aceita qualquer login (desenvolvimento sem backend). */
    mode: "mock" | "jwt";
    endpoints: AuthEndpoints;
    /** Papéis concedidos no modo mock. */
    mockRoles: string[];
  };
  /** Medição. `ga4` é o ID do Google Analytics 4 (G-...); vazio desliga. */
  analytics?: { ga4?: string };
  realtime: {
    /** Liga a conexão com o hub (no `auth.mode: "mock"` fica sempre desligada). */
    enabled: boolean;
    /**
     * URL do hub SignalR. Relativa (`/hubs/app`) usa a origem da `apiBaseUrl`
     * quando ela for absoluta; senão, a origem do app (proxy do Vite em dev).
     */
    hubUrl: string;
  };
}

const defaults: AppConfig = {
  appName: "crialume",
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? "/api",
  features: {},
  disabledModules: [],
  auth: {
    mode: "jwt",
    endpoints: {
      signIn: "/auth/sign-in",
      refresh: "/auth/refresh",
      signOut: "/auth/sign-out",
      me: "/auth/me",
      changePassword: "/auth/change-password",
    },
    mockRoles: [],
  },
  realtime: {
    enabled: false,
    hubUrl: "/hubs/app",
  },
};

/** URL final do hub a partir da config (ver `AppConfig.realtime.hubUrl`). */
export function resolveHubUrl(config: Pick<AppConfig, "apiBaseUrl" | "realtime">): string {
  const { hubUrl } = config.realtime;
  if (/^https?:\/\//i.test(hubUrl) || !/^https?:\/\//i.test(config.apiBaseUrl)) return hubUrl;
  return new URL(hubUrl, config.apiBaseUrl).toString();
}

declare global {
  interface Window {
    /** Pedido do `config.json` disparado pelo `index.html`, antes do bundle chegar. */
    __crialumeConfig?: Promise<Response>;
  }
}

export function loadAppConfig(): Promise<AppConfig> {
  const early = window.__crialumeConfig;
  delete window.__crialumeConfig;
  return loadRuntimeConfig({
    url: `${import.meta.env.BASE_URL}config.json`,
    defaults,
    ...(early ? { fetchImpl: (() => early) as typeof fetch } : {}),
  });
}
